import { type PropsWithChildren, type ReactNode } from 'react';
import {
  type RefinementListProps,
  useClearRefinements,
  useRefinementList,
  useToggleRefinement } from
'react-instantsearch';
import { type RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import { Button, RadioGroup, RadioGroupItem } from '~/apps/product-plus/components/cxy/cxy.ts';

import styles from '../styles/BetTypeFilters.module.css';

export type HandicapRefinementProps = PropsWithChildren<
  RefinementListProps & {
    childrenPlacement?: 'before' | 'after';
    isBooleanField?: boolean;
    isDisabled?: boolean;
    label?: string;
    trackChange?: (value: string) => void;
    transformFieldLabel?: (item: RefinementListItem) => ReactNode;
  }>;


export const HandicapRefinement = ({
  children,
  childrenPlacement = 'after',
  isBooleanField = false,
  isDisabled = false,
  label,
  trackChange,
  transformFieldLabel,
  ...refinementProps
}: HandicapRefinementProps) => {
  const { refine: clearRefinement } = useClearRefinements({
    includedAttributes: [refinementProps.attribute]
  });

  const { refine: toggleTrue, value: trueValue } = useToggleRefinement({
    attribute: refinementProps.attribute,
    on: true
  });

  const { refine: toggleFalse, value: falseValue } = useToggleRefinement({
    attribute: refinementProps.attribute,
    on: false
  });

  const { canToggleShowMore, isShowingMore, items, refine, toggleShowMore } = useRefinementList({
    ...refinementProps
  });

  if (isBooleanField) {
    const getCurrentValue = () => {
      if (trueValue.isRefined) return 'true';
      if (falseValue.isRefined) return 'false';
      return 'all';
    };

    const handleValueChange = (value: string) => {
      trackChange?.(value);

      clearRefinement();
      if (value === 'true') {
        toggleTrue({ isRefined: false });
      } else if (value === 'false') {
        toggleFalse({ isRefined: false });
      }
    };

    return (
      <RadioGroup
        aria-label={label}
        className={styles.select}
        disabled={isDisabled}
        onValueChange={handleValueChange}
        value={getCurrentValue()}>

        {childrenPlacement === 'before' && children}
        <RadioGroupItem key="all" value="all">
          All
        </RadioGroupItem>
        <RadioGroupItem key="true" value="true">
          Yes
        </RadioGroupItem>
        <RadioGroupItem key="false" value="false">
          No
        </RadioGroupItem>
        {childrenPlacement === 'after' && children}
      </RadioGroup>);

  }

  const selectedValue = items.find((item) => item.isRefined)?.value ?? 'all';

  const handleValueChange = (value: string) => {
    trackChange?.(value);
    if (value === 'all') {
      clearRefinement();
    } else {
      refine(value);
    }
  };

  return (
    <>
      <RadioGroup
        aria-label={label}
        className={styles.select}
        disabled={isDisabled}
        onValueChange={handleValueChange}
        value={selectedValue}>

        {childrenPlacement === 'before' && children}
        {items.length > 1 &&
        <RadioGroupItem key="all" value="all">
            All
          </RadioGroupItem>
        }
        {items.map((item) => {
          return (
            <RadioGroupItem key={item.value} value={item.value}>
              {transformFieldLabel ? transformFieldLabel(item) : item.label}
            </RadioGroupItem>);

        })}
        {childrenPlacement === 'after' && children}
      </RadioGroup>
      {canToggleShowMore &&
      <Button onClick={toggleShowMore} variant="ghost">
          {isShowingMore ? 'Show less' : 'Show more'}
        </Button>
      }
    </>);

};